import { Config } from "../../Config";
import { ILaya } from "../../ILaya";
import { LayaEnv } from "../../LayaEnv";
import { NodeFlags } from "../Const";
import { DrawTextureCmd } from "../display/cmd/DrawTextureCmd";
import { Event } from "../events/Event";
import { SerializeUtil } from "../loaders/SerializeUtil";
import { Color } from "../maths/Color";
import { Point } from "../maths/Point";
import { Loader } from "../net/Loader";
import { Component } from "./Component";
export var AnimationWrapMode;
(function (AnimationWrapMode) {
    AnimationWrapMode[AnimationWrapMode["Positive"] = 0] = "Positive";
    AnimationWrapMode[AnimationWrapMode["Reverse"] = 1] = "Reverse";
    AnimationWrapMode[AnimationWrapMode["PingPong"] = 2] = "PingPong";
})(AnimationWrapMode || (AnimationWrapMode = {}));
export var AnimationStretchMode;
(function (AnimationStretchMode) {
    AnimationStretchMode[AnimationStretchMode["None"] = 0] = "None";
    AnimationStretchMode[AnimationStretchMode["Fill"] = 1] = "Fill";
    AnimationStretchMode[AnimationStretchMode["ResizeToFit"] = 2] = "ResizeToFit";
})(AnimationStretchMode || (AnimationStretchMode = {}));
export class FrameAnimation extends Component {
    constructor() {
        super();
        this.repeatDelay = 0;
        this.timeScale = 1;
        this._wrapMode = 0;
        this._loop = true;
        this._frame = 0;
        this._autoPlay = true;
        this._stretchMode = 0;
        this._source = "";
        this._playing = false;
        this._count = 0;
        this._index = 0;
        this._elapsed = 0;
        this._loadId = 0;
        this.interval = Config.animationInterval;
        this._frames = [];
        this._drawCmds = [];
        this._delays = [];
        this._offset = new Point();
        this._color = new Color(1, 1, 1, 1);
        this._singleton = false;
        this.runInEditor = true;
    }
    get frame() {
        return LayaEnv.isPlaying ? this._frame : this._index;
    }
    set frame(value) {
        var _a;
        this._index = this._frame = value;
        this.drawFrame();
        if ((_a = this._labels) === null || _a === void 0 ? void 0 : _a[value])
            this.owner.event(Event.LABEL, this._labels[value]);
    }
    get frames() {
        return this._frames;
    }
    set frames(value) {
        var _a;
        if (this._drawCmd) {
            (_a = this.owner._graphics) === null || _a === void 0 ? void 0 : _a.removeCmd(this._drawCmd);
            this._drawCmd = null;
        }
        for (let cmd of this._drawCmds) {
            cmd.lock = false;
            cmd.recover();
        }
        this._drawCmds.length = 0;
        this._frames.length = 0;
        if (value != null && value.length > 0) {
            this._frames.push(...value);
            let dx = 0, dy = 0;
            if (this._stretchMode === AnimationStretchMode.None) {
                dx = this._offset.x;
                dy = this._offset.y;
            }
            let stretch = this._stretchMode === AnimationStretchMode.Fill;
            for (let tex of value) {
                let cmd = stretch ? DrawTextureCmd.create(tex, 0, 0, 1, 1, null, 1, null, null, null, true)
                    : DrawTextureCmd.create(tex, dx, dy);
                cmd.lock = true;
                this._drawCmds.push(cmd);
            }
            this._count = this._frames.length;
            this._elapsed = 0;
            if (this._wrapMode === AnimationWrapMode.Reverse)
                this._frame = this._count - 1;
            else {
                this._reversed = false;
                this._frame = (this._index > 0 && this._index < this._count) ? this._index : 0;
            }
            if (this._stretchMode === AnimationStretchMode.ResizeToFit) {
                let w = this.width, h = this.height;
                if (w > 0 && h > 0 || LayaEnv.isPlaying) {
                    this._changingSize = true;
                    this.owner.size(w, h);
                    this._changingSize = false;
                }
            }
            this.drawFrame();
        }
        else {
            this._count = 0;
        }
    }
    get frameDelays() {
        return this._delays;
    }
    get isPlaying() {
        return this._playing;
    }
    get autoPlay() {
        return this._autoPlay;
    }
    set autoPlay(value) {
        this._autoPlay = value;
        if (this.enabled) {
            if (value)
                this.play();
            else
                this.stop();
        }
    }
    get wrapMode() {
        return this._wrapMode;
    }
    set wrapMode(value) {
        if (this._wrapMode != value) {
            this._wrapMode = value;
            if (this._playing) {
                if (value === AnimationWrapMode.Reverse)
                    this._reversed = true;
                else if (value === AnimationWrapMode.Positive)
                    this._reversed = false;
            }
        }
    }
    get loop() {
        return this._loop;
    }
    set loop(value) {
        if (this._loop != value) {
            this._loop = value;
            if (value && !this._playing && this._autoPlay)
                this.play();
        }
    }
    get stretchMode() {
        return this._stretchMode;
    }
    set stretchMode(value) {
        if (this._changingSize)
            return;
        if (this._stretchMode != value) {
            this._stretchMode = value;
            if (this._count > 0) {
                if (this._stretchMode === AnimationStretchMode.ResizeToFit) {
                    let w = this.width, h = this.height;
                    if (w > 0 && h > 0 || LayaEnv.isPlaying) {
                        this._changingSize = true;
                        this.owner.size(w, h);
                        this._changingSize = false;
                    }
                }
                this.applyStretchMode();
                this.drawFrame();
            }
        }
    }
    get offset() {
        return this._offset;
    }
    set offset(value) {
        this._offset.copy(value);
        this.applyStretchMode();
        this.drawFrame();
    }
    get color() {
        return this._color;
    }
    set color(value) {
        this._color = value;
        this.drawFrame();
    }
    get width() {
        return this._count > 0 ? this._frames[0].sourceWidth : 0;
    }
    get height() {
        return this._count > 0 ? this._frames[0].sourceHeight : 0;
    }
    applyStretchMode() {
        if (this._stretchMode === AnimationStretchMode.Fill) {
            for (let cmd of this._drawCmds) {
                cmd.x = cmd.y = 0;
                cmd.width = cmd.height = 1;
                cmd.percent = true;
            }
        }
        else {
            let dx = 0, dy = 0;
            if (this._stretchMode === AnimationStretchMode.None) {
                dx = this._offset.x;
                dy = this._offset.y;
            }
            for (let cmd of this._drawCmds) {
                cmd.x = dx;
                cmd.y = dy;
                if (cmd.texture) {
                    cmd.width = cmd.texture.sourceWidth;
                    cmd.height = cmd.texture.sourceHeight;
                }
                cmd.percent = false;
            }
        }
    }
    play() {
        if (this._playing)
            return;
        this._playing = true;
        this._elapsed = 0;
        this._reversed = this._wrapMode === AnimationWrapMode.Reverse;
    }
    stop() {
        this._playing = false;
        if (!LayaEnv.isPlaying) {
            this._frame = this._index;
            this.drawFrame();
        }
    }
    _onEnable() {
        if (this._autoPlay)
            this.play();
    }
    _onDestroy() {
        super._onDestroy();
        this.frames = null;
        if (this._atlas) {
            if (this.owner._getBit(NodeFlags.EDITING_NODE))
                this._atlas.off("reload", this, this.onAtlasReload);
            this._atlas = null;
        }
    }
    onUpdate() {
        var _a;
        if (!this._playing || this._count == 0)
            return;
        let dt = ILaya.timer.delta;
        if (dt > 100)
            dt = 100;
        if (this.timeScale != 1)
            dt *= this.timeScale;
        let frame = this._frame;
        this._elapsed += dt;
        let tt = this.interval;
        if (this._reversed) {
            if (frame > 0)
                tt += this._delays[frame - 1] || 0;
            else
                tt += this.repeatDelay;
        }
        else {
            tt += this._delays[frame] || 0;
            if (frame === this._count - 1)
                tt += this.repeatDelay;
        }
        if (this._elapsed < tt)
            return;
        this._elapsed -= tt;
        if (this._elapsed > this.interval)
            this._elapsed = this.interval;
        let emit = false;
        if (this._reversed) {
            frame--;
            if (frame < 0) {
                if (this._loop) {
                    if (this._wrapMode === AnimationWrapMode.PingPong) {
                        this._reversed = false;
                        frame = 1;
                    }
                    else
                        frame = this._count - 1;
                    emit = true;
                }
                else {
                    frame = 0;
                    this._playing = false;
                    emit = true;
                }
            }
        }
        else {
            frame++;
            if (frame > this._count - 1) {
                if (this._loop) {
                    if (this._wrapMode === AnimationWrapMode.PingPong) {
                        this._reversed = true;
                        frame = Math.max(0, this._count - 2);
                    }
                    else
                        frame = 0;
                    emit = true;
                }
                else {
                    frame = this._count - 1;
                    this._playing = false;
                    emit = true;
                }
            }
        }
        this._frame = frame;
        if (this._playing) {
            this.drawFrame();
            if ((_a = this._labels) === null || _a === void 0 ? void 0 : _a[frame])
                this.owner.event(Event.LABEL, this._labels[frame]);
        }
        if (emit)
            this.owner.event(Event.COMPLETE);
    }
    drawFrame() {
        let cmd = this._drawCmds[this._frame];
        if (cmd != this._drawCmd)
            this._drawCmd = this.owner.graphics.replaceCmd(this._drawCmd, cmd);
        if (this._drawCmd)
            this._drawCmd.color = this._color.getABGR();
    }
    get source() {
        return this._source;
    }
    set source(value) {
        if (value == null)
            value = "";
        this._source = value;
        this.load();
    }
    get images() {
        return this._images;
    }
    set images(value) {
        this._images = value;
        this.load();
    }
    load() {
        if (this._atlas) {
            if (this.owner._getBit(NodeFlags.EDITING_NODE))
                this._atlas.off("reload", this, this.onAtlasReload);
            this._atlas = null;
        }
        if (this._source)
            this.loadAtlas(this._source);
        else if (this._images && this._images.length > 0)
            this.loadImages(this._images);
        else
            this.onLoaded(null, ++this._loadId);
    }
    loadImages(urls) {
        let loadId = ++this._loadId;
        let textures = urls.map(url => Loader.getRes(url));
        if (textures.indexOf(null) === -1) {
            this.frames = textures;
            this.owner.event(Event.LOADED);
        }
        else {
            ILaya.loader.load(urls).then((textures) => {
                if (loadId != this._loadId || this.destroyed)
                    return;
                this.frames = textures;
                this.owner.event(Event.LOADED);
            });
        }
        return this;
    }
    loadAtlas(url) {
        let loadId = ++this._loadId;
        let atlas = SerializeUtil.isDeserializing ? null : Loader.getRes(url, Loader.ATLAS);
        if (atlas)
            this.onLoaded(atlas, loadId);
        else
            ILaya.loader.load(url, Loader.ATLAS).then(atlas => this.onLoaded(atlas, loadId));
        return this;
    }
    setAtlas(res) {
        this.onLoaded(res, ++this._loadId);
    }
    onLoaded(atlas, loadId) {
        var _a, _b;
        if (loadId != this._loadId || this.destroyed)
            return;
        this._atlas = atlas;
        if (atlas) {
            if (this.owner._getBit(NodeFlags.EDITING_NODE))
                this._atlas.on("reload", this, this.onAtlasReload);
            let ani = atlas.animation;
            if (ani) {
                this.interval = ani.interval;
                this.repeatDelay = (_a = ani.repeatDelay) !== null && _a !== void 0 ? _a : 0;
                this.wrapMode = (_b = ani.wrapMode) !== null && _b !== void 0 ? _b : 0;
                this._delays.length = 0;
                if (ani.frameDelays)
                    this._delays.push(...ani.frameDelays);
            }
            this.frames = atlas.frames;
        }
        else
            this.frames = null;
        this.owner.event(Event.LOADED);
    }
    onAtlasReload() {
        this.onLoaded(this._atlas, this._loadId);
    }
}
